import { InMemoryCache, NormalizedCacheObject } from "@apollo/client";
import { FilterMattersQuery } from "~/gql/graphql";
import {
  autoFocusAssumptionLocalVar,
  autoFocusDisbursementLocalVar,
  autoFocusDiscountLocalVar,
  autoFocusExclusionLocalVar,
  autoFocusPhaseLocalVar,
  autoFocusUpliftLocalVar,
  autoFocusTechnologyChargeLocalVar,
} from "./local-variables";

import { StrictTypedTypePolicies } from "./__generated__/apollo-helpers";

declare global {
  interface Window {
    __APOLLO_STATE__: NormalizedCacheObject;
  }
}
const WINDOW = (typeof window === "undefined" ? { __APOLLO_STATE__: {} } : window) as Window;

const typePolicies: StrictTypedTypePolicies = {
  BaseMatterResult: {
    keyFields: ["reference"], // This is added due to Parent Matters and Matter being stored in seperate tables and the ID can overlap
  },
  Assumption: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusAssumptionLocalVar();
        },
      },
    },
  },
  Exclusion: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusExclusionLocalVar();
        },
      },
    },
  },
  Disbursement: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusDisbursementLocalVar();
        },
      },
    },
  },
  Uplift: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusUpliftLocalVar();
        },
      },
    },
  },
  Discount: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusDiscountLocalVar();
        },
      },
    },
  },
  TechnologyCharge: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusTechnologyChargeLocalVar();
        },
      },
    },
  },
  Phase: {
    fields: {
      autoFocus: {
        read() {
          return autoFocusPhaseLocalVar();
        },
      },
    },
  },

  Query: {
    fields: {
      findAccruedFeesForUser: {
        keyArgs: ["filter", ["matterStatus"]],
        merge(existing, incoming) {
          const existingAccruedFees = existing?.items?.slice(0) || [];

          const existingMatterIds = existingAccruedFees
            .map((x: any) => x.matterId)
            .filter((id: any) => id != null);

          const { items: incomingItems } = incoming;

          incomingItems.forEach((incomingItem: any) => {
            if (incomingItem.matterId && !existingMatterIds.includes(incomingItem.matterId)) {
              existingAccruedFees.push(incomingItem);
            }
          });

          const updatedAccruedFees = existingAccruedFees?.map((existingItem: any) => {
            const incomingItem = incomingItems.find(
              (item: any) => item.matterId === existingItem.matterId
            );
            return {
              ...existingItem,
              collaborators: [...incomingItem.collaborators],
            };
          });

          return {
            ...existing,
            items: updatedAccruedFees,
            total: incoming.total,
          };
        },
        read(existing) {
          return existing;
        },
      },
      findAccruedFees: {
        keyArgs: ["filter", ["matterStatus", "organisationUnitId"]],
        merge(existing, incoming) {
          const existingAccruedFees = existing?.items?.slice(0) || [];

          const existingMatterIds = existingAccruedFees
            .map((x: any) => x.matterId)
            .filter((id: any) => id != null);

          const { items: incomingItems } = incoming;

          incomingItems.forEach((item: any) => {
            if (item.matterId && !existingMatterIds.includes(item.matterId)) {
              existingAccruedFees.push(item);
            }
          });

          return {
            ...existing,
            items: existingAccruedFees,
            total: incoming.total,
          };
        },
        read(existing) {
          return existing;
        },
      },
      filteredMatters: {
        keyArgs: ["input", ["filter", "offset", "limit"]],
        merge(
          existing: FilterMattersQuery["filteredMatters"] | undefined,
          incoming: FilterMattersQuery["filteredMatters"],
          { args, readField }
        ) {
          //Copy

          const existingMattersCopy = existing?.matters ? existing?.matters?.slice(0) : [];

          const offset = args?.input.offset || 0;

          const existingMatters = existingMattersCopy.reduce((cls: string[], c: any) => {
            cls.push(readField("reference", c) as any);
            return cls;
          }, []);

          for (let i = 0; i < incoming.matters.length; ++i) {
            const cacheRef = readField("reference", incoming.matters[i]);
            if (typeof cacheRef === "string") {
              const alreadyInCache = existingMatters.includes(cacheRef);
              if (!alreadyInCache) {
                existingMattersCopy[offset + i] = incoming.matters[i];
              }
            }
          }

          return {
            matters: existingMattersCopy,
            count: incoming.count,
          };
        },
        read(existing) {
          return existing;
        },
      },
      filteredClients: {
        keyArgs: ["filter", ["search"]],
        merge(existing, incoming, { readField }) {
          const existingClients = existing?.clients?.slice(0) || [];

          const existingClientIds = existingClients.reduce((cls: number[], c: any) => {
            cls.push(readField("id", c) as any);
            return cls;
          }, []);

          const { clients: incomingClients } = incoming;

          incomingClients.forEach((client: any) => {
            if (!existingClientIds.includes(readField("id", client) as any)) {
              existingClients.push(client);
            }
          });

          return {
            ...existing,
            clients: existingClients,
            total: incoming.total,
          };
        },
        read(existing) {
          return existing;
        },
      },
      filteredExternalMatters: {
        keyArgs: ["input", ["filter", "offset", "limit"]],
        merge(existing, incoming, { readField }) {
          const existingExternalMatters: any[] = existing?.externalMatters?.slice(0) || [];

          const existingClientIds = existingExternalMatters.reduce((cls: number[], c: any) => {
            cls.push(readField("id", c) as any);
            return cls;
          }, []);

          const { externalMatters: incomingExternalMatters } = incoming;

          incomingExternalMatters.forEach((externalMatter: any) => {
            if (!existingClientIds.includes(readField("id", externalMatter) as any)) {
              existingExternalMatters.push(externalMatter);
            }
          });

          return {
            ...existing,
            externalMatters: existingExternalMatters,
            total: incoming.total,
          };
        },
        read(existing) {
          return existing;
        },
      },
      matterTemplates: {
        keyArgs: ["templateType"],
        merge(existing, incoming, { args }) {
          const offset = args?.offset || 0;
          const merged = existing?.items ? existing?.items?.slice(0) : [];
          for (let i = 0; i < incoming.items.length; ++i) {
            merged[offset + i] = incoming.items[i];
          }
          return {
            items: merged,
            total: incoming.total,
          };
        },
        read(existing) {
          return existing?.items;
        },
      },
    },
  },
};

export const cache = new InMemoryCache({
  typePolicies,
}).restore(WINDOW.__APOLLO_STATE__);
